import React, { PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import { ExtendedMonitoredTv, MonitoredMcdmMeasure, MonitoredMeasure } from '../../../../model/SOL49';
import { CountType, FlightId, TimePeriod } from '../../../../model/CORE';
import { CheckCircleFilled } from '@ant-design/icons';
import { AtcSector } from './AtcSector';
import TimePeriodUtils from '../../../../common/util/TimePeriodUtils';
import './AtcSectors.less';
import MeasureUtils from '../../../../common/util/MeasureUtils';

interface AtcSectorsProps {
    currentTime: Date;
    monitoredTvs: Array<ExtendedMonitoredTv>;
    monitoredMeasures: Array<MonitoredMeasure>;
    monitoredMcdmMeasures: Array<MonitoredMcdmMeasure>;
    airspaceId: string;
    openingPeriod: TimePeriod;
    countType: CountType;
    tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> };
    selectedPeriodByTv?: Map<string, Array<TimePeriod>>;
    onSelectedPeriodByTvChange?: (selectedPeriodByTv: Map<string, Array<TimePeriod>>) => void;
}

export class AtcSectors extends PureComponent<AtcSectorsProps> {
    handleTvClick = (monitoredTv: ExtendedMonitoredTv) => {
        return e => {
            const tvId = monitoredTv?.refLocation?.id;

            // sector that is not selected or partially selected during opening => fully selected
            // sector that is fully selected => not selected
            const periods = this.props.selectedPeriodByTv?.get(tvId) || [];
            const intersectPeriodIdx = periods.findIndex(period =>
                TimePeriodUtils.intersects(this.props.openingPeriod, period),
            );
            if (intersectPeriodIdx !== -1) {
                if (TimePeriodUtils.contains(periods[intersectPeriodIdx], this.props.openingPeriod)) {
                    // was already selected - removing value (not selected)
                    periods.splice(intersectPeriodIdx, 1);
                } else {
                    // was partially selected - selects full opening period
                    periods[intersectPeriodIdx] = TimePeriodUtils.copy(this.props.openingPeriod);
                }
            } else {
                // was not selected - add full opening period
                periods.push(TimePeriodUtils.copy(this.props.openingPeriod));
            }

            // Send updated map to controlling component
            const selectedPeriodByTv = new Map<string, Array<TimePeriod>>(this.props.selectedPeriodByTv);
            if (periods.length > 0) {
                selectedPeriodByTv.set(tvId, periods);
            } else {
                selectedPeriodByTv.delete(tvId);
            }
            this.props.onSelectedPeriodByTvChange?.(selectedPeriodByTv);
        };
    };

    render() {
        const monitoredTv = this.props.monitoredTvs?.[0];
        if (!monitoredTv) {
            return (
                <Flex
                    key={`no-count-for-${this.props.airspaceId}`}
                    className={`atc-sectors`}
                    direction="vertical"></Flex>
            );
        }
        const tvId = monitoredTv?.refLocation?.id;

        const periods = this.props.selectedPeriodByTv?.get(tvId) || [];
        const intersectPeriodIdx = periods.findIndex(period =>
            TimePeriodUtils.intersects(this.props.openingPeriod, period),
        );
        const selected = intersectPeriodIdx !== -1;
        const selectorClassName = selected !== undefined ? 'show-on-hover' : 'hide';

        const occupancyPeriods = this.props.tvsOccupancyPeriods?.[tvId];

        const regulationPeriods = this.props.monitoredMeasures
            ?.filter(
                monitoredMeasure =>
                    MeasureUtils.getTvLocation(monitoredMeasure.measure).id === monitoredTv.refLocation.id,
            )
            .map(monitoredMeasure => {
                if (MeasureUtils.isRegulationOrMcdmOnly(monitoredMeasure.measure)) {
                    return MeasureUtils.getRegulationPeriods(monitoredMeasure.measure);
                }
            });

        const proposalRegulationPeriods = this.props.monitoredMcdmMeasures
            ?.filter(
                monitoredMcdmMeasure =>
                    MeasureUtils.getTvLocation(monitoredMcdmMeasure.measure).id === monitoredTv.refLocation.id,
            )
            .map(monitoredMcdmMeasure => {
                if (MeasureUtils.isRegulationOrMcdmOnly(monitoredMcdmMeasure.measure)) {
                    return MeasureUtils.getRegulationPeriods(monitoredMcdmMeasure.measure);
                }
            });

        const shortSectorName = tvId.substring(4, tvId.length);

        return (
            <Flex className={`atc-sectors ${selected ? ' selected' : ''}`} direction="vertical">
                <Flex className="label" onClick={this.handleTvClick(monitoredTv)}>
                    <Flex
                        className={'check-circle ' + selectorClassName}
                        style={{ padding: '0 0.25em', alignSelf: 'center', visibility: 'hidden' }}>
                        <CheckCircleFilled />
                    </Flex>
                    <Flex grow={false}>
                        {tvId}
                        {this.props.monitoredTvs.length > 1 ? '*' : ''}
                    </Flex>
                </Flex>
                <Flex>
                    <AtcSector
                        key={`atc-sector-${this.props.airspaceId}`}
                        currentTime={this.props.currentTime}
                        countType={this.props.countType}
                        monitoredTv={monitoredTv}
                        openingPeriod={this.props.openingPeriod}
                        occupancyPeriods={occupancyPeriods}
                        regulationPeriods={regulationPeriods}
                        proposalRegulationPeriods={proposalRegulationPeriods}
                        selectedPeriodByTv={this.props.selectedPeriodByTv}
                        onSelectedPeriodByTvChange={this.props.onSelectedPeriodByTvChange}
                    />
                </Flex>
            </Flex>
        );
    }
}
